import { ChevronRight, PanelRightOpen, UsersRound } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import type { ToolUIProps } from "./ToolUIContext";
import { type CohortSummary, formatCohortCount, summarizeCohort } from "./cohortSummary";
import { findMcpAppState, MCP_APP_OPEN_SOURCE, useMcpAppTabLink } from "./useMcpAppsPane";

const COHORT_SERVER_ID = "cohort_explorer";
const MAX_FUNNEL_CRITERIA = 4;

export const CohortCreatedToolUI = (props: ToolUIProps) => (
  <CohortSummaryCard {...props} verb="Created" />
);

export const CohortUpdatedToolUI = (props: ToolUIProps) => (
  <CohortSummaryCard {...props} verb="Updated" />
);

interface CohortSummaryCardProps extends ToolUIProps {
  verb: "Created" | "Updated";
}

/**
 * The chat's entry point to a cohort the agent built or changed. The latest
 * change to a cohort shows its live summary and an "Open cohort" button;
 * earlier changes fold to one line. Outside the chat (the read-only views)
 * there is no sidebar, so the card shows the tool result with no opener.
 */
const CohortSummaryCard = ({ toolCallId, result, isRunning, verb }: CohortSummaryCardProps) => {
  const snapshot = result ? findMcpAppState(result, COHORT_SERVER_ID) : null;
  const link = useMcpAppTabLink(COHORT_SERVER_ID, snapshot ? String(snapshot.id) : "");

  if (!snapshot) return isRunning ? <CardSkeleton /> : null;

  const tab = link?.tab;
  const handleOpen = link?.tab ? () => link.open(MCP_APP_OPEN_SOURCE.CARD) : undefined;

  if (tab && handleOpen && tab.lastToolCallId !== toolCallId) {
    return <CohortChangeRow summary={summarizeCohort(snapshot)} verb={verb} onOpen={handleOpen} />;
  }

  const live = tab && isCohortState(tab.state) ? tab.state : snapshot;
  return (
    <CohortCard
      summary={summarizeCohort(live)}
      refreshFailed={tab?.refreshFailed ?? false}
      onOpen={handleOpen}
    />
  );
};

interface CohortCardProps {
  summary: CohortSummary;
  refreshFailed: boolean;
  onOpen?: () => void;
}

const CohortCard = ({ summary, refreshFailed, onOpen }: CohortCardProps) => {
  const count = formatCohortCount(summary, refreshFailed);
  const shown = summary.criteria.slice(0, MAX_FUNNEL_CRITERIA);
  const hiddenCount = summary.criteria.length - shown.length;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
      {/* Two columns, so the funnel's line sits under the icon and its rows start at the name. */}
      <div className="grid grid-cols-[auto_1fr] gap-x-3 px-4 pt-3 pb-2">
        <UsersRound className="size-4.5 self-center text-primary" aria-hidden />
        <div className="flex min-w-0 items-center gap-3">
          <p className="min-w-0 flex-1 truncate text-sm font-semibold">{summary.name}</p>
          {count && (
            <span
              className={cn(
                "shrink-0 text-sm",
                summary.pending ? "italic text-muted-foreground" : "font-semibold"
              )}
            >
              {count}
            </span>
          )}
        </div>
        {(summary.startingCount != null || shown.length > 0) && (
          <>
            <span className="mx-auto mt-2 w-px bg-border" aria-hidden />
            <ol className="mt-2 flex min-w-0 flex-col gap-1 text-xs">
              {summary.startingCount != null && (
                <FunnelRow label="Starting population" count={summary.startingCount} />
              )}
              {shown.map((criterion, index) => (
                <FunnelRow
                  key={`${index}-${criterion.label}`}
                  label={criterion.label}
                  count={criterion.participantCount}
                />
              ))}
              {hiddenCount > 0 && (
                <li className="text-muted-foreground">
                  +{hiddenCount} more {hiddenCount === 1 ? "criterion" : "criteria"}
                </li>
              )}
            </ol>
          </>
        )}
      </div>
      {onOpen && (
        <div className="flex justify-end px-4 pb-3">
          <Button size="sm" variant="outline" onClick={onOpen}>
            <PanelRightOpen aria-hidden />
            Open cohort
          </Button>
        </div>
      )}
    </div>
  );
};

const FunnelRow = ({ label, count }: { label: string; count: number | null }) => (
  <li className="flex items-baseline gap-2">
    <span className="min-w-0 truncate text-muted-foreground">{label}</span>
    <span className="shrink-0 tabular-nums text-foreground">
      {count == null ? "—" : count.toLocaleString()}
    </span>
  </li>
);

interface CohortChangeRowProps {
  summary: CohortSummary;
  verb: CohortSummaryCardProps["verb"];
  onOpen: () => void;
}

const CohortChangeRow = ({ summary, verb, onOpen }: CohortChangeRowProps) => {
  const count = formatCohortCount(summary);
  const text = `${verb} ${summary.name}${count ? `: ${count}` : ""}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open cohort ${summary.name}`}
      className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted"
    >
      <UsersRound className="size-3.5 shrink-0 text-primary" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{text}</span>
      <ChevronRight className="size-3.5 shrink-0" aria-hidden />
    </button>
  );
};

const CardSkeleton = () => (
  <div className="animate-pulse overflow-hidden rounded-xl border border-border bg-card px-4 py-3">
    <div className="h-3 w-24 rounded bg-muted" />
    <div className="mt-2 h-4 w-40 rounded bg-muted" />
  </div>
);

function isCohortState(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}
